/* eslint-disable import/prefer-default-export */

import { isVoltageTelemetryInput } from './fuelPolicyApiMapping';

export const FUEL_STATUSES = ['DRAFT', 'ACTIVE', 'ARCHIVED'];
/** Tier 0/1 fields locked when status is ACTIVE; Tier 2 tolerances (incl. tank capacity) stay editable. */
export const LOCKED_FIELD_KEYS_WHEN_ACTIVE = new Set([
    'name',
    'telemetryInput',
    'voltageMin',
    'voltageMax',
    'tankCapacityDetectionEnabled',
    'tableMapEnabled',
    'equationMapEnabled',
    'fuelMapRows',
    'equationMap',
    'percentageMapping',
    'idleLhr',
    'cityL100km',
    'avgL100km',
    'hwyL100km',
    'penaltyPct',
    'penaltyIntervalKmh',
]);

const num = (v) => {
    if (v === '' || v === null || v === undefined) return NaN;
    const n = typeof v === 'number' ? v : parseFloat(String(v));
    return Number.isFinite(n) ? n : NaN;
};

/** Treat blank/invalid inputs as 0 so empty A₂ does not break validation. */
const coeff = (v) => {
    const n = num(v);
    return Number.isFinite(n) ? n : 0;
};

const sortedIntervals = (rows) => [...rows].sort((a, b) => num(a.fuelLevelFrom) - num(b.fuelLevelFrom));

function evaluateFuelMapEquation(a2, a1, a0, levelL) {
    const L = num(levelL);
    if (!Number.isFinite(L)) return NaN;
    const c2 = coeff(a2);
    const c1 = coeff(a1);
    const c0 = coeff(a0);
    return (c2 * L * L) + (c1 * L) + c0;
}

/** Sample mapped (%) across L = 0 … tankCapacityL (litres). */
function validateEquationRange(a2, a1, a0, tankCapacityL, steps = 48) {
    const cap = num(tankCapacityL);
    if (!Number.isFinite(cap) || cap <= 0) {
        return {
            ok: false, minV: NaN, maxV: NaN, reason: 'tank',
        };
    }
    const c2 = coeff(a2);
    const c1 = coeff(a1);
    const c0 = coeff(a0);
    if (c2 === 0 && c1 === 0 && c0 === 0) {
        return {
            ok: false, minV: NaN, maxV: NaN, reason: 'zero',
        };
    }
    let minV = Infinity;
    let maxV = -Infinity;
    for (let i = 0; i <= steps; i += 1) {
        const L = (cap * i) / steps;
        const m = evaluateFuelMapEquation(c2, c1, c0, L);
        if (!Number.isFinite(m)) {
            return {
                ok: false, minV: NaN, maxV: NaN, reason: 'nan',
            };
        }
        minV = Math.min(minV, m);
        maxV = Math.max(maxV, m);
    }
    if (minV < 0 || maxV > 100) {
        return {
            ok: false, minV, maxV, reason: 'range',
        };
    }
    return {
        ok: true, minV, maxV,
    };
}

function equationValidationMessage(er, tank) {
    if (!Number.isFinite(num(tank)) || num(tank) <= 0) {
        return 'Set a valid tank capacity (L) before validating the fuel map equation.';
    }
    if (er.reason === 'zero') {
        return 'Equation must have at least one non-zero coefficient (a₂, a₁, or a₀).';
    }
    if (er.reason === 'range' && Number.isFinite(er.minV) && Number.isFinite(er.maxV)) {
        return `Equation maps to ${er.minV.toFixed(1)}–${er.maxV.toFixed(1)}% across the tank; it must stay within 0–100%. For a linear map use a₂=0, a₁=${(100 / num(tank)).toFixed(6)}, a₀=0.`;
    }
    return 'Equation must have a non-zero coefficient and map to 0–100% across the tank range.';
}

/**
 * Table mode (litre input): rows { fuelLevelFrom, fuelLevelTo, mappedValue }; tank capacity in liters.
 * Level columns in L; mapped value in %. Validates ≥3 rows, monotonic from, no overlaps, coverage 0..tankCapacityL.
 */
export function validateFuelMapTable(rows, tankCapacityL) {
    const cap = num(tankCapacityL);
    const errs = [];
    if (!Array.isArray(rows) || rows.length < 3) {
        errs.push('Fuel map table requires at least 3 rows.');
        return { ok: false, errors: errs };
    }
    const sorted = sortedIntervals(rows);
    for (let i = 0; i < sorted.length; i += 1) {
        const from = num(sorted[i].fuelLevelFrom);
        const to = num(sorted[i].fuelLevelTo);
        const mv = num(sorted[i].mappedValue);
        if (!Number.isFinite(from) || !Number.isFinite(to) || !Number.isFinite(mv)) {
            errs.push('Each map row needs numeric from, to, and mapped value.');
            return { ok: false, errors: errs };
        }
        if (from >= to) {
            errs.push('Each row must have fuel level “from” less than “to”.');
        }
        if (mv < 0 || mv > 100) {
            errs.push('Mapped values must be between 0 and 100.');
        }
    }
    for (let i = 1; i < sorted.length; i += 1) {
        const prevTo = num(sorted[i - 1].fuelLevelTo);
        const curFrom = num(sorted[i].fuelLevelFrom);
        if (curFrom < prevTo) {
            errs.push('Fuel map rows must not overlap and “from” must be non-decreasing.');
        }
        if (curFrom < num(sorted[i - 1].fuelLevelFrom)) {
            errs.push('fuelLevelFrom values must be monotonically increasing.');
        }
    }
    if (!Number.isFinite(cap) || cap <= 0) {
        errs.push('Set a valid tank capacity before validating the fuel map.');
        return { ok: false, errors: errs };
    }
    const firstFrom = num(sorted[0].fuelLevelFrom);
    const lastTo = num(sorted[sorted.length - 1].fuelLevelTo);
    if (firstFrom > 0.001) {
        errs.push('Fuel map must cover the full range starting from 0 L.');
    }
    if (lastTo < cap - 0.001) {
        errs.push(`Fuel map must extend to the full tank capacity (${cap} L).`);
    }
    if (lastTo > cap + 0.001) {
        errs.push(`Fuel map must not extend beyond tank capacity (${cap} L). Adjust map rows or increase tank capacity.`);
    }
    return { ok: errs.length === 0, errors: errs };
}

/**
 * Table mode (percentage input): level columns in %; mapped value in L.
 * Validates ≥3 rows, level ranges within 0–100%, mapped litres within [0, tankCapacityL].
 */
export function validatePercentageFuelMapTable(rows, tankCapacityL) {
    const cap = num(tankCapacityL);
    const errs = [];
    if (!Array.isArray(rows) || rows.length < 3) {
        errs.push('Fuel map table requires at least 3 rows.');
        return { ok: false, errors: errs };
    }
    const sorted = sortedIntervals(rows);
    for (let i = 0; i < sorted.length; i += 1) {
        const from = num(sorted[i].fuelLevelFrom);
        const to = num(sorted[i].fuelLevelTo);
        const mv = num(sorted[i].mappedValue);
        if (!Number.isFinite(from) || !Number.isFinite(to) || !Number.isFinite(mv)) {
            errs.push('Each map row needs numeric from, to, and mapped value.');
            return { ok: false, errors: errs };
        }
        if (from >= to) {
            errs.push('Each row must have fuel level “from” less than “to”.');
        }
        if (from < 0 || to > 100) {
            errs.push('Fuel level ranges must be between 0 and 100%.');
        }
        if (mv < 0) {
            errs.push('Mapped values must be zero or greater.');
        }
    }
    for (let i = 1; i < sorted.length; i += 1) {
        const prevTo = num(sorted[i - 1].fuelLevelTo);
        const curFrom = num(sorted[i].fuelLevelFrom);
        if (curFrom < prevTo) {
            errs.push('Fuel map rows must not overlap and “from” must be non-decreasing.');
        }
        if (curFrom < num(sorted[i - 1].fuelLevelFrom)) {
            errs.push('fuelLevelFrom values must be monotonically increasing.');
        }
    }
    if (!Number.isFinite(cap) || cap <= 0) {
        errs.push('Set a valid tank capacity before validating the fuel map.');
        return { ok: false, errors: errs };
    }
    for (let i = 0; i < sorted.length; i += 1) {
        const mv = num(sorted[i].mappedValue);
        if (mv > cap + 0.001) {
            errs.push(`Mapped values must not exceed tank capacity (${cap} L).`);
            break;
        }
    }
    const firstFrom = num(sorted[0].fuelLevelFrom);
    const lastTo = num(sorted[sorted.length - 1].fuelLevelTo);
    if (firstFrom > 0.001) {
        errs.push('Fuel map must cover the full range starting from 0%.');
    }
    if (lastTo < 100 - 0.001) {
        errs.push('Fuel map must extend to 100%.');
    }
    if (lastTo > 100 + 0.001) {
        errs.push('Fuel map must not extend beyond 100%.');
    }
    return { ok: errs.length === 0, errors: errs };
}

/**
 * Voltage map table: rows map voltage (V) → litres (L).
 * Validates ≥3 rows, strictly increasing voltage, litres within [0, tankCapacityL].
 */
export function validateVoltageFuelMapTable(rows, tankCapacityL) {
    const cap = num(tankCapacityL);
    const errs = [];
    if (!Array.isArray(rows) || rows.length < 3) {
        errs.push('Voltage map table requires at least 3 rows.');
        return { ok: false, errors: errs };
    }
    const sorted = sortedIntervals(rows);
    for (let i = 0; i < sorted.length; i += 1) {
        const from = num(sorted[i].fuelLevelFrom);
        const to = num(sorted[i].fuelLevelTo);
        const litres = num(sorted[i].mappedValue);
        if (!Number.isFinite(from) || !Number.isFinite(to) || !Number.isFinite(litres)) {
            errs.push('Each map row needs numeric voltage from, to, and litres.');
            return { ok: false, errors: errs };
        }
        if (from >= to) {
            errs.push('Each row must have voltage “from” less than “to”.');
        }
        if (litres < 0) {
            errs.push('Litres must be zero or greater.');
        }
    }
    for (let i = 1; i < sorted.length; i += 1) {
        const prevTo = num(sorted[i - 1].fuelLevelTo);
        const curFrom = num(sorted[i].fuelLevelFrom);
        if (curFrom < prevTo) {
            errs.push('Voltage map rows must not overlap and “from” must be non-decreasing.');
        }
        if (curFrom < num(sorted[i - 1].fuelLevelFrom)) {
            errs.push('Voltage values must be monotonically increasing.');
        }
    }
    if (!Number.isFinite(cap) || cap <= 0) {
        errs.push('Set a valid tank capacity before validating the voltage map.');
        return { ok: false, errors: errs };
    }
    for (let i = 0; i < sorted.length; i += 1) {
        const litres = num(sorted[i].mappedValue);
        if (litres > cap + 0.001) {
            errs.push(`Litres must not exceed tank capacity (${cap} L).`);
            break;
        }
    }
    return { ok: errs.length === 0, errors: errs };
}

function validateFuelMapTableForForm(form, tank) {
    const rows = form.fuelMapRows || [];
    if (isVoltageTelemetryInput(form.telemetryInput)) {
        return validateVoltageFuelMapTable(rows, tank);
    }
    if (form.telemetryInput === 'percentage') {
        return validatePercentageFuelMapTable(rows, tank);
    }
    return validateFuelMapTable(rows, tank);
}

function validateVoltageRange(form) {
    const errors = {};
    const vMin = num(form.voltageMin);
    const vMax = num(form.voltageMax);

    if (!Number.isFinite(vMin)) {
        errors.voltageMin = 'Minimum voltage is required.';
    }
    if (!Number.isFinite(vMax)) {
        errors.voltageMax = 'Maximum voltage is required.';
    }
    if (Number.isFinite(vMin) && Number.isFinite(vMax) && vMin >= vMax) {
        errors.voltageMax = 'Maximum voltage must be greater than minimum.';
    }
    return errors;
}

const hasFilledValue = (value) => value !== '' && value != null;

/** Validate the Fuel Ledger controls whose runtime semantics require positive values. */
function validateFuelLedgerProfileSettings(form, { requireCore = false } = {}) {
    const errors = {};
    const positiveCoreFields = [
        ['gapCapMin', 'Offline gap cap'],
        ['drainSpanMin', 'Drain window'],
        ['maxBurnRateLh', 'Max burn rate'],
    ];

    positiveCoreFields.forEach(([key, label]) => {
        const value = num(form[key]);
        if (!Number.isFinite(value)) {
            if (hasFilledValue(form[key])) {
                errors[key] = 'Must be a valid number.';
            } else if (requireCore) {
                errors[key] = 'Required.';
            }
        } else if (value <= 0) {
            errors[key] = `${label} must be greater than 0.`;
        }
    });

    const microRefill = num(form.microRefillL);
    if (hasFilledValue(form.microRefillL)) {
        if (!Number.isFinite(microRefill)) {
            errors.microRefillL = 'Must be a valid number.';
        } else if (microRefill <= 0) {
            errors.microRefillL = 'Must be greater than 0 L or left blank to disable.';
        } else {
            const refillThreshold = num(form.refillDetectionThresholdL);
            if (Number.isFinite(refillThreshold) && microRefill >= refillThreshold) {
                errors.microRefillL = 'Must be less than the refill threshold.';
            }
        }
    }

    return errors;
}

/** DRAFT / ARCHIVED: name required; empty fields allowed; filled fields validated. */
export function validateFuelPolicyDraft(form) {
    const errors = {};
    if (!form.name?.trim()) {
        errors.name = 'Policy name is required.';
    }
    if (form.status && form.status !== 'DRAFT' && form.status !== 'ACTIVE' && form.status !== 'ARCHIVED') {
        errors.status = 'Invalid status.';
    }

    const tank = num(form.tankCapacityL);
    if (hasFilledValue(form.tankCapacityL)) {
        if (!Number.isFinite(tank)) {
            errors.tankCapacityL = 'Tank capacity must be a valid number.';
        } else if (tank < 5 || tank > 2000) {
            errors.tankCapacityL = 'Tank capacity must be between 5 and 2000 L.';
        }
    }

    const refill = num(form.refillDetectionThresholdL);
    const theft = num(form.theftDrainThresholdL);
    const spike = num(form.spikeFilterVolumeL);
    const sloshDur = num(form.sloshSettleDurationSec);
    const sloshTol = num(form.sloshToleranceL);

    [
        'refillDetectionThresholdL',
        'theftDrainThresholdL',
        'spikeFilterVolumeL',
        'sloshSettleDurationSec',
        'sloshToleranceL',
        'idleLhr',
        'cityL100km',
        'avgL100km',
        'hwyL100km',
        'penaltyPct',
        'penaltyIntervalKmh',
    ].forEach((key) => {
        if (hasFilledValue(form[key]) && !Number.isFinite(num(form[key]))) {
            errors[key] = 'Must be a valid number.';
        }
    });

    if (Number.isFinite(tank) && tank >= 5 && tank <= 2000) {
        const quarter = tank / 4;
        if (Number.isFinite(spike) && (spike < 1 || spike > 50)) {
            errors.spikeFilterVolumeL = 'Spike filter volume must be 1–50 L.';
        }
        if (Number.isFinite(refill) && (refill < 1 || refill > quarter)) {
            errors.refillDetectionThresholdL = `Must be between 1 and ${quarter.toFixed(2)} L (tank/4).`;
        }
        if (Number.isFinite(refill) && Number.isFinite(spike) && refill < spike) {
            errors.refillDetectionThresholdL = 'Must be greater than or equal to spike filter volume.';
        }
        if (Number.isFinite(theft) && (theft < 1 || theft > quarter)) {
            errors.theftDrainThresholdL = `Must be between 1 and ${quarter.toFixed(2)} L (tank/4).`;
        }
    }

    if (Number.isFinite(sloshDur) && sloshDur <= 0) {
        errors.sloshSettleDurationSec = 'Must be greater than 0 seconds.';
    }
    if (Number.isFinite(sloshTol) && (sloshTol < 0.1 || sloshTol > 10)) {
        errors.sloshToleranceL = 'Must be between 0.1 and 10 L.';
    }
    Object.assign(errors, validateFuelLedgerProfileSettings(form));

    if (isVoltageTelemetryInput(form.telemetryInput)) {
        Object.assign(errors, validateVoltageRange(form));
        if (!Number.isFinite(tank) && !errors.tankCapacityL) {
            errors.tankCapacityL = 'Tank capacity is required for voltage calibration.';
        }
    }

    if (form.tableMapEnabled) {
        const rows = form.fuelMapRows || [];
        if (rows.length >= 3 && Number.isFinite(tank)) {
            const tableResult = validateFuelMapTableForForm(form, tank);
            if (!tableResult.ok) {
                const mapErr = tableResult.errors[0] || 'Invalid fuel map table.';
                errors.fuelMap = mapErr;
                if (mapErr.includes('tank capacity')) {
                    errors.tankCapacityL = mapErr;
                }
            }
        } else if (rows.length > 0) {
            rows.forEach((row, index) => {
                const rowHasValue = hasFilledValue(row.fuelLevelFrom)
                    || hasFilledValue(row.fuelLevelTo)
                    || hasFilledValue(row.mappedValue);
                if (!rowHasValue) {
                    return;
                }
                const from = num(row.fuelLevelFrom);
                const to = num(row.fuelLevelTo);
                const mappedValue = num(row.mappedValue);
                if (!Number.isFinite(from) || !Number.isFinite(to) || !Number.isFinite(mappedValue)) {
                    errors.fuelMap = errors.fuelMap
                        || `Fuel map row ${index + 1}: enter valid numeric from, to, and mapped values.`;
                } else if (from >= to) {
                    errors.fuelMap = errors.fuelMap
                        || `Fuel map row ${index + 1}: "from" must be less than "to".`;
                } else if (isVoltageTelemetryInput(form.telemetryInput)) {
                    if (mappedValue < 0 || (Number.isFinite(tank) && mappedValue > tank)) {
                        errors.fuelMap = errors.fuelMap || `Litres must be between 0 and ${tank} L.`;
                    }
                } else if (form.telemetryInput === 'percentage') {
                    if (mappedValue < 0 || (Number.isFinite(tank) && mappedValue > tank)) {
                        errors.fuelMap = errors.fuelMap || `Mapped values must be between 0 and ${tank} L.`;
                    }
                } else if (mappedValue < 0 || mappedValue > 100) {
                    errors.fuelMap = errors.fuelMap || 'Mapped values must be between 0 and 100.';
                }
            });
        }
    }

    if (form.equationMapEnabled) {
        const equationMap = form.equationMap || {};
        ['a2', 'a1', 'a0'].forEach((key) => {
            if (hasFilledValue(equationMap[key]) && !Number.isFinite(num(equationMap[key]))) {
                errors.fuelMap = errors.fuelMap || 'Equation coefficients must be valid numbers.';
            }
        });
    }

    return { ok: Object.keys(errors).length === 0, errors };
}

export function fuelPolicyStatusBlockedMessage(assetsAttached, targetStatus) {
    const count = Math.max(0, Number(assetsAttached) || 0);
    if (count === 0) {
        return '';
    }
    const action = targetStatus === 'ARCHIVED' ? 'archiving' : 'changing to draft';
    if (count === 1) {
        return `1 asset is still using this fuel policy. Unlink it before ${action}.`;
    }
    return `${count} assets are still using this fuel policy. Unlink them before ${action}.`;
}

/** Block DRAFT / ARCHIVED when assets are still linked to this policy. */
export function validatePolicyStatusWithAssets(form, assetsAttached) {
    if (form.status !== 'DRAFT' && form.status !== 'ARCHIVED') {
        return { ok: true, errors: {} };
    }
    const message = fuelPolicyStatusBlockedMessage(assetsAttached, form.status);
    if (message) {
        return { ok: false, errors: { status: message } };
    }
    return { ok: true, errors: {} };
}

export function validateAllRequiredForActive(form) {
    const errors = {};
    if (!form.name?.trim()) errors.name = 'Policy name is required.';

    const tank = num(form.tankCapacityL);
    if (!Number.isFinite(tank)) errors.tankCapacityL = 'Tank capacity is required.';
    const refill = num(form.refillDetectionThresholdL);
    const theft = num(form.theftDrainThresholdL);
    const spike = num(form.spikeFilterVolumeL);
    const sloshDur = num(form.sloshSettleDurationSec);
    const sloshTol = num(form.sloshToleranceL);

    if (!Number.isFinite(refill)) errors.refillDetectionThresholdL = 'Required.';
    if (!Number.isFinite(theft)) errors.theftDrainThresholdL = 'Required.';
    if (!Number.isFinite(spike)) errors.spikeFilterVolumeL = 'Required.';
    if (!Number.isFinite(sloshDur)) errors.sloshSettleDurationSec = 'Required.';
    if (!Number.isFinite(sloshTol)) errors.sloshToleranceL = 'Required.';

    ['idleLhr', 'cityL100km', 'avgL100km', 'hwyL100km', 'penaltyPct', 'penaltyIntervalKmh'].forEach((k) => {
        if (!Number.isFinite(num(form[k]))) errors[k] = 'Required.';
    });

    if (Number.isFinite(tank)) {
        if (tank < 5 || tank > 2000) errors.tankCapacityL = 'Tank capacity must be between 5 and 2000 L.';
        const quarter = tank / 4;
        if (Number.isFinite(spike) && (spike < 1 || spike > 50)) errors.spikeFilterVolumeL = 'Spike filter volume must be 1–50 L.';
        if (Number.isFinite(refill)) {
            if (refill < 1 || refill > quarter) errors.refillDetectionThresholdL = `Must be between 1 and ${quarter.toFixed(2)} L (tank/4).`;
            if (Number.isFinite(spike) && refill < spike) {
                errors.refillDetectionThresholdL = 'Must be greater than or equal to spike filter volume.';
            }
        }
        if (Number.isFinite(theft) && (theft < 1 || theft > quarter)) {
            errors.theftDrainThresholdL = `Must be between 1 and ${quarter.toFixed(2)} L (tank/4).`;
        }
    }
    if (Number.isFinite(sloshDur) && sloshDur <= 0) {
        errors.sloshSettleDurationSec = 'Must be greater than 0 seconds.';
    }
    if (Number.isFinite(sloshTol) && (sloshTol < 0.1 || sloshTol > 10)) {
        errors.sloshToleranceL = 'Must be between 0.1 and 10 L.';
    }
    Object.assign(errors, validateFuelLedgerProfileSettings(form, { requireCore: true }));

    if (!form.telemetryInput) {
        errors.telemetryInput = 'Select a level signal unit (Litre, Percentage, Litre & Percentage, or Voltage).';
    }
    if (isVoltageTelemetryInput(form.telemetryInput)) {
        Object.assign(errors, validateVoltageRange(form));
        if (!Number.isFinite(tank)) {
            errors.tankCapacityL = 'Tank capacity is required for voltage calibration (used to derive %).';
        }
    }
    if (!isVoltageTelemetryInput(form.telemetryInput)
        && !form.tableMapEnabled
        && !form.equationMapEnabled
        && !form.tankCapacityDetectionEnabled) {
        errors.fuelMap = 'Choose a level mapping (MAP TABLE, MAP EQUATION, or Fuel tank capacity detection).';
    }
    if (form.tableMapEnabled) {
        const t = validateFuelMapTableForForm(form, tank);
        if (!t.ok) {
            const mapErr = t.errors[0] || 'Invalid fuel map table.';
            errors.fuelMap = mapErr;
            if (mapErr.includes('tank capacity')) errors.tankCapacityL = mapErr;
        }
    }
    // Equation 0–100% range check disabled — coefficients may be set freely while
    // calculation rules are being revised (see validateEquationRange).
    // if (form.equationMapEnabled) {
    //     const eq = form.equationMap || {};
    //     const er = validateEquationRange(eq.a2, eq.a1, eq.a0, tank);
    //     if (!er.ok) errors.fuelMap = equationValidationMessage(er, tank);
    // }

    return { ok: Object.keys(errors).length === 0, errors };
}

const mergeValidationResults = (...results) => {
    const errors = results.reduce((acc, result) => ({ ...acc, ...result.errors }), {});
    return { ok: results.every((result) => result.ok), errors };
};

/** Route save validation by policy status. */
export function validateFuelPolicyForSave(form, { assetsAttached = 0 } = {}) {
    const statusCheck = validatePolicyStatusWithAssets(form, assetsAttached);
    if (!statusCheck.ok) {
        return statusCheck;
    }
    if (form.status === 'ACTIVE') {
        return mergeValidationResults(
            validateFuelPolicyDraft(form),
            validateAllRequiredForActive(form),
        );
    }
    return validateFuelPolicyDraft(form);
}

export { buildFuelPolicyPayload } from './fuelPolicyApiMapping';
